import { Controller } from "@hotwired/stimulus"

/**
 * Permissions Form Controller
 *
 * Manages the permissions configuration modal lifecycle:
 * - Opens the modal when triggered by "Configure Permissions" button
 * - Fetches current user data and policies from the API
 * - Dispatches custom event to notify user-permissions controller
 * - Handles modal show/hide using Bootstrap 5 Modal API
 *
 * Connects to data-controller="forms--permissions-form"
 */
export default class extends Controller {

    static values = {
        userId: String
    }

    connect() {
        // Initialize Bootstrap modal instance
        const modalElement = document.getElementById('permissionsModal')

        if (modalElement) {
            // Check if there's already a Bootstrap modal instance
            const existingInstance = bootstrap.Modal.getInstance(modalElement)

            if (existingInstance) {
                this.modal = existingInstance
            } else {
                this.modal = new bootstrap.Modal(modalElement, {
                    backdrop: true,      // Allow closing by clicking outside
                    keyboard: true       // Allow closing with ESC key
                })
            }
        } else {
            console.error('Permissions modal element not found')
        }
    }

    /**
     * Open the permissions modal
     * Triggered by "Configure Permissions" button click
     *
     * @param {Event} event - Click event from button
     */
    async open(event) {
        // Extract user_id from event params
        // The button should have: data-forms--permissions-form-user-id-param="123"
        const userId = event.params?.userId || this.userIdValue

        if (!userId) {
            console.error('No user ID provided to permissions modal')
            return
        }

        try {
            // Fetch current user data from API
            // This includes the user's email and current policies
            const response = await fetch(`/api/user/describe_user?id=${ encodeURIComponent(userId) }`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            })

            if (!response.ok) {
                throw new Error(`HTTP ${ response.status }: ${ response.statusText }`)
            }

            const userData = await response.json()

            // Dispatch custom event to user-permissions controller
            // This event carries the user data so the permissions controller
            // can populate the form with current selections
            window.dispatchEvent(new CustomEvent('forms--permissions-form:opened', {
                detail: {
                    userId: userId,
                    name: userData.data.name || 'Unknown User',
                    email: userData.data.email || 'Unknown User',
                    admin: userData.data.admin,  // Binary value: 1000 for admin, nil for non-admin
                    currentPolicies: userData.data.policies || { actions: [], locales: [] }
                },
                bubbles: true,
                cancelable: true
            }))

            // Show the modal using Bootstrap 5 API
            if (this.modal) {
                this.modal.show()
            }

        } catch (error) {
            console.error('Error loading user data for permissions modal:', error)

            // Show error notification to user
            if (window.sendFailureNotification) {
                window.sendFailureNotification('Unable to load user permissions. Please try again.')
            } else {
                alert('Unable to load user permissions. Please try again.')
            }
        }
    }

    /**
     * Close the permissions modal
     * Triggered by Cancel button or close button (X)
     */
    close() {
        const modalElement = document.getElementById('permissionsModal')
        if (!modalElement) {
            console.error('Modal element not found')
            return
        }

        // Try to get existing Bootstrap modal instance
        let modalInstance = bootstrap.Modal.getInstance(modalElement)

        if (modalInstance) {
            try {
                modalInstance.hide()
                return
            } catch (error) {
                console.warn('Bootstrap hide() failed, falling back to DOM manipulation:', error)
            }
        }

        // Fallback: Direct DOM manipulation to close modal
        modalElement.classList.remove('show')
        modalElement.style.display = 'none'
        modalElement.setAttribute('aria-hidden', 'true')
        modalElement.removeAttribute('aria-modal')
        modalElement.removeAttribute('role')

        // Remove backdrop if it exists
        const backdrop = document.querySelector('.modal-backdrop')
        if (backdrop) {
            backdrop.remove()
        }

        // Remove modal-open class from body and restore scrolling
        document.body.classList.remove('modal-open')
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
    }

    /**
     * Cleanup when controller disconnects
     */
    disconnect() {
        // Safely dispose of Bootstrap modal instance to prevent memory leaks
        if (this.modal) {
            try {
                this.modal.dispose()
            } catch (error) {
                // Silently handle disposal errors - modal may already be disposed or corrupted
                // This can happen if the modal was closed via DOM manipulation
            }
        }
    }
}
